import { ArrowLeftIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import {
  ChevronDownIcon,
  ChevronRightIcon,
  ShieldCheckIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/solid';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import type { RootState } from '../../../store/store';
import {
  addNafExclusion,
  getNafCodesHierarchy,
  getNafExclusions,
  removeNafExclusion,
} from '../../../utils/api';
import { JWTAuthorization, JWTHeader } from '../../../utils/JWTHeader';
import { ROUTES } from '../../../utils/routes';
import Badge from '../../common/Badge';
import { showToast } from '../../common/Notification';
import Spinner from '../../common/Spinner';

interface NafNode {
  code: string;
  level: string;
  label: string;
  children?: Record<string, NafNode>;
}

interface NafExclusionData {
  id: number;
  nafCode: string;
  nafLevel: string;
  createdAt: string;
}

function NafExclusion() {
  const navigate = useNavigate();
  const company = useSelector((state: RootState) => state.auth.company);
  const jwt = useSelector((state: RootState) => state.auth.token);

  const [nafHierarchy, setNafHierarchy] = useState<Record<string, NafNode> | null>(null);
  const [nafLabelsMap, setNafLabelsMap] = useState<Record<string, string>>({});
  const [exclusions, setExclusions] = useState<NafExclusionData[]>([]);
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Build a flat map of code -> label from the hierarchy
  const buildLabelsMap = useCallback((hierarchy: Record<string, NafNode>): Record<string, string> => {
    const map: Record<string, string> = {};
    
    const traverse = (node: NafNode) => {
      map[node.code] = node.label;
      if (node.children) {
        Object.values(node.children).forEach(traverse);
      }
    };
    
    Object.values(hierarchy).forEach(traverse);
    return map;
  }, []);

  // Get label for a NAF code from the map
  const getNafLabel = useCallback((code: string): string => {
    return nafLabelsMap[code] || code;
  }, [nafLabelsMap]);

  const fetchData = useCallback(async() => {
    if (!company?.id || !jwt) {return;}

    setIsLoading(true);
    setError(null);

    try {
      const [hierarchyResponse, exclusionsResponse] = await Promise.all([
        fetch(getNafCodesHierarchy(), JWTHeader(jwt)),
        fetch(getNafExclusions(company.id), JWTHeader(jwt)),
      ]);

      if (!hierarchyResponse.ok || !exclusionsResponse.ok) {
        throw new Error('Failed to fetch data');
      }

      const hierarchyData = await hierarchyResponse.json();
      const exclusionsData = await exclusionsResponse.json();

      setNafHierarchy(hierarchyData);
      setNafLabelsMap(buildLabelsMap(hierarchyData));
      setExclusions(exclusionsData);
    } catch (err) {
      console.error('Error fetching NAF data:', err);
      setError('Erreur lors du chargement des données NAF');
      showToast('error', 'Erreur lors du chargement des données');
    } finally {
      setIsLoading(false);
    }
  }, [company?.id, jwt]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const toggleNode = (code: string) => {
    setExpandedNodes((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(code)) {
        newSet.delete(code);
      } else {
        newSet.add(code);
      }
      return newSet;
    });
  };

  const isParentExcluded = useCallback(
    (code: string): boolean => {
      for (const exclusion of exclusions) {
        if (code.startsWith(exclusion.nafCode) && code !== exclusion.nafCode) {
          return true;
        }
      }
      return false;
    },
    [exclusions],
  );

  // Get the prefix to use for matching child NAF codes (mirrors backend logic)
  const getNafPrefix = (nafCode: string): string => {
    const trimmedCode = nafCode.trim();
    // Division (2 digits): 68 -> prefix "68."
    // Group (4 chars with dot): 68.1 -> prefix "68.1"
    // Class (5 chars with dot): 68.10 -> prefix "68.10"
    // Subclass (6+ chars with letter): 68.10Z -> prefix "68.10Z"
    if (/^\d{2}$/.test(trimmedCode)) {
      return `${trimmedCode}.`;
    }
    return trimmedCode;
  };

  // Check if a NAF code is a child of another NAF code
  const isChildOfCode = (childCode: string, parentCode: string): boolean => {
    if (childCode === parentCode) {return false;}
    const prefix = getNafPrefix(parentCode);
    return childCode.startsWith(prefix);
  };

  const handleAddExclusion = async(code: string) => {
    if (!company?.id || !jwt || isSaving) {return;}

    setIsSaving(true);
    try {
      const response = await fetch(addNafExclusion(company.id), {
        method: 'POST',
        headers: {
          Authorization: JWTAuthorization(jwt),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ nafCode: code }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to add exclusion');
      }

      const newExclusion = await response.json();
      // Remove any child exclusions that are now covered by this parent exclusion
      // (backend already removed them, we need to sync the frontend state)
      setExclusions((prev) => {
        const filtered = prev.filter((e) => !isChildOfCode(e.nafCode, code));
        return [...filtered, newExclusion];
      });
      showToast('success', `Code NAF ${code} ajouté aux exclusions`);
    } catch (err: unknown) {
      console.error('Error adding exclusion:', err);
      showToast('error', err instanceof Error ? err.message : 'Erreur lors de l\'ajout de l\'exclusion');
    } finally {
      setIsSaving(false);
    }
  };

  const handleRemoveExclusion = async(code: string) => {
    if (!company?.id || !jwt || isSaving) {return;}

    setIsSaving(true);
    try {
      const response = await fetch(removeNafExclusion(company.id, code), {
        method: 'DELETE',
        headers: {
          Authorization: JWTAuthorization(jwt),
        },
      });

      if (!response.ok) {
        throw new Error('Failed to remove exclusion');
      }

      setExclusions((prev) => prev.filter((e) => e.nafCode !== code));
      showToast('success', `Code NAF ${code} retiré des exclusions`);
    } catch (err) {
      console.error('Error removing exclusion:', err);
      showToast('error', 'Erreur lors de la suppression de l\'exclusion');
    } finally {
      setIsSaving(false);
    }
  };

  const getLevelLabel = (level: string): string => {
    switch (level) {
      case 'division':
        return 'Division';
      case 'group':
        return 'Groupe';
      case 'class':
        return 'Classe';
      case 'subclass':
        return 'Sous-classe';
      default:
        return level;
    }
  };

  const getLevelColor = (level: string): string => {
    switch (level) {
      case 'division':
        return 'bg-blue-100 text-blue-800';
      case 'group':
        return 'bg-green-100 text-green-800';
      case 'class':
        return 'bg-yellow-100 text-yellow-800';
      case 'subclass':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Find the path to a specific NAF code in the hierarchy
  const findPathToCode = useCallback((
    nodes: Record<string, NafNode>,
    targetCode: string,
    currentPath: string[] = [],
  ): string[] | null => {
    for (const node of Object.values(nodes)) {
      if (node.code === targetCode) {
        return currentPath;
      }
      if (node.children) {
        const result = findPathToCode(node.children, targetCode, [...currentPath, node.code]);
        if (result) {
          return result;
        }
      }
    }
    return null;
  }, []);

  // Auto-expand nodes to show all exclusions when data is loaded
  useEffect(() => {
    if (!nafHierarchy || exclusions.length === 0) {return;}
    
    const nodesToExpand = new Set<string>();
    
    exclusions.forEach(exclusion => {
      const pathToExclusion = findPathToCode(nafHierarchy, exclusion.nafCode);
      if (pathToExclusion) {
        pathToExclusion.forEach(code => nodesToExpand.add(code));
      }
    });
    
    if (nodesToExpand.size > 0) {
      setExpandedNodes(prev => {
        const newSet = new Set(prev);
        nodesToExpand.forEach(code => newSet.add(code));
        return newSet;
      });
    }
  }, [nafHierarchy, exclusions, findPathToCode]);

  // Normalize text for search: lowercase, remove accents, remove dots
  const normalizeForSearch = useCallback((text: string): string => {
    return text
      .toLowerCase()
      // Remove diacritics/accents using Unicode normalization
      .normalize('NFD')
      .replaceAll(/[\u0300-\u036f]/g, '')
      // Remove dots/periods
      .replaceAll('.', '');
  }, []);

  // Filter nodes based on search query
  const nodeMatchesSearch = useCallback((node: NafNode, query: string): boolean => {
    if (!query) {return true;}
    const normalizedQuery = normalizeForSearch(query);
    const normalizedCode = normalizeForSearch(node.code);
    const normalizedLabel = normalizeForSearch(node.label);
    
    // Check if code or label matches (with normalization)
    if (normalizedCode.includes(normalizedQuery) || normalizedLabel.includes(normalizedQuery)) {
      return true;
    }
    
    // Check children
    if (node.children) {
      return Object.values(node.children).some(child => nodeMatchesSearch(child, query));
    }
    
    return false;
  }, [normalizeForSearch]);

  // Expand nodes that match search
  useEffect(() => {
    if (!searchQuery || !nafHierarchy) {return;}
    
    const nodesToExpand = new Set<string>();
    
    const findMatchingNodes = (nodes: Record<string, NafNode>, parentCodes: string[] = []) => {
      Object.values(nodes).forEach(node => {
        if (nodeMatchesSearch(node, searchQuery)) {
          // Expand all parent nodes
          parentCodes.forEach(code => nodesToExpand.add(code));
          
          // If this node has matching children, expand it too
          if (node.children && Object.values(node.children).some(child => nodeMatchesSearch(child, searchQuery))) {
            nodesToExpand.add(node.code);
            findMatchingNodes(node.children, [...parentCodes, node.code]);
          }
        }
      });
    };
    
    findMatchingNodes(nafHierarchy);
    setExpandedNodes(nodesToExpand);
  }, [searchQuery, nafHierarchy, nodeMatchesSearch]);

  const filteredHierarchy = useMemo(() => {
    if (!nafHierarchy || !searchQuery) {return nafHierarchy;}
    
    const filterNodes = (nodes: Record<string, NafNode>): Record<string, NafNode> => {
      const filtered: Record<string, NafNode> = {};
      
      Object.entries(nodes).forEach(([key, node]) => {
        if (nodeMatchesSearch(node, searchQuery)) {
          filtered[key] = {
            ...node,
            children: node.children ? filterNodes(node.children) : undefined,
          };
        }
      });
      
      return filtered;
    };
    
    return filterNodes(nafHierarchy);
  }, [nafHierarchy, searchQuery, nodeMatchesSearch]);

  const renderNafNode = (node: NafNode, depth: number = 0): React.ReactNode => {
    const hasChildren = node.children && Object.keys(node.children).length > 0;
    const isExpanded = expandedNodes.has(node.code);
    const isExcluded = exclusions.some((e) => e.nafCode === node.code);
    const isParentExcludedFlag = isParentExcluded(node.code);
    const paddingLeft = depth * 24;

    return (
      <div key={node.code}>
        <div
          className={`flex items-center border-b border-gray-100 px-4 py-3 hover:bg-gray-50 ${
            isExcluded ? 'bg-red-50' : isParentExcludedFlag ? 'bg-orange-50' : ''
          }`}
          style={{ paddingLeft: `${paddingLeft + 16}px` }}
        >
          {hasChildren ? (
            <button
              onClick={() => toggleNode(node.code)}
              className="mr-3 rounded p-1 hover:bg-gray-200"
            >
              {isExpanded ? (
                <ChevronDownIcon className="h-5 w-5 text-gray-600" />
              ) : (
                <ChevronRightIcon className="h-5 w-5 text-gray-600" />
              )}
            </button>
          ) : (
            <div className="mr-3 w-7" />
          )}

          <span className={`mr-3 font-mono text-sm font-semibold ${isParentExcludedFlag ? 'text-gray-400' : 'text-rgpd-blue-dark'}`}>
            {node.code}
          </span>

          <span className={`mr-3 rounded px-2 py-1 text-xs ${getLevelColor(node.level)}`}>
            {getLevelLabel(node.level)}
          </span>

          <span className={`flex-1 text-sm ${isParentExcludedFlag ? 'text-gray-400' : 'text-gray-700'}`}>
            {node.label}
          </span>

          {isExcluded ? (
            <button
              onClick={() => handleRemoveExclusion(node.code)}
              disabled={isSaving}
              className="flex items-center gap-1 rounded bg-red-100 px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-200 disabled:opacity-50"
            >
              <TrashIcon className="h-4 w-4" />
              Retirer
            </button>
          ) : isParentExcludedFlag ? (
            <span className="px-3 text-xs text-orange-600 italic">
              Exclu via parent
            </span>
          ) : (
            <button
              onClick={() => handleAddExclusion(node.code)}
              disabled={isSaving}
              className="flex items-center gap-1 rounded bg-rgpd-blue px-3 py-1.5 text-sm font-medium text-white hover:bg-rgpd-blue-dark disabled:opacity-50"
            >
              <ShieldCheckIcon className="h-4 w-4" />
              Exclure
            </button>
          )}
        </div>

        {hasChildren && isExpanded && (
          <div>
            {Object.values(node.children!).map((child) => renderNafNode(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  const handleGoBack = () => {
    try {
      if (window.history.state && window.history.state.idx > 0) {
        navigate(-1);
      } else {
        navigate(ROUTES.HOME);
      }
    } catch (navError) {
      console.error('Navigation error:', navError);
      navigate(ROUTES.HOME);
    }
  };

  return (
    <div className="rounded-lg bg-white p-8 shadow-md">
      {/* Header */}
      <div className="mb-8 flex items-center justify-between">
        <button
          onClick={handleGoBack}
          className="flex items-center gap-2 text-rgpd-blue transition-colors hover:text-rgpd-blue-dark"
        >
          <ArrowLeftIcon className="h-5 w-5" />
          <span>Retour</span>
        </button>
      </div>

      <div className="leading-relaxed text-slate-800">
        {/* Title Banner */}
        <div className="relative mb-6 overflow-hidden rounded-2xl bg-rgpd-blue px-8 py-5 text-center text-white">
          <div className="relative z-10">
            <div className="mb-2 flex items-center justify-center gap-3">
              <ShieldCheckIcon className="h-8 w-8" />
              <h1 className="text-xl font-bold tracking-tight">
                Surveillance exclusion assurance (code NAF)
              </h1>
            </div>
            <div className="mx-auto max-w-2xl text-base font-normal opacity-90">
              Configurez les codes NAF à exclure de la surveillance assurance pour votre entreprise
            </div>
          </div>
        </div>

        {/* Info box */}
        <div className="mb-6 rounded-lg border border-blue-200 bg-blue-50 p-4">
          <h3 className="mb-2 font-semibold text-blue-800">Comment ça fonctionne ?</h3>
          <ul className="space-y-1 text-sm text-blue-700">
            <li>
              • Sélectionnez les codes NAF des activités à exclure de la surveillance assurance
            </li>
            <li>
              • En sélectionnant une division (ex: 68), tous les groupes, classes et sous-classes seront automatiquement exclus
            </li>
            <li>
              • Les entreprises avec ces codes NAF apparaîtront dans la page "Surveillance exclusion assurance"
            </li>
          </ul>
        </div>

        {/* Current exclusions summary */}
        {exclusions.length > 0 && (
          <div className="mb-6 rounded-lg border border-gray-200 bg-white p-4">
            <h3 className="mb-3 font-semibold text-gray-800">
              Exclusions actuelles ({exclusions.length})
            </h3>
            <div className="flex flex-wrap gap-2">
              {exclusions.map((exclusion) => (
                <div
                  key={exclusion.nafCode}
                  className="flex items-center gap-2 rounded-full bg-red-100 px-3 py-1.5 text-sm text-red-800"
                >
                  <span className="font-mono font-semibold">{exclusion.nafCode}</span>
                  <span className="text-xs">({getLevelLabel(exclusion.nafLevel)})</span>
                  <span className="max-w-[200px] truncate text-xs text-red-600" title={getNafLabel(exclusion.nafCode)}>
                    - {getNafLabel(exclusion.nafCode)}
                  </span>
                  <button
                    onClick={() => handleRemoveExclusion(exclusion.nafCode)}
                    disabled={isSaving}
                    className="rounded-full p-0.5 hover:bg-red-200"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* NAF Hierarchy */}
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
          <div className="border-b bg-gray-50 px-4 py-3">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <h3 className="font-semibold text-gray-800">Hiérarchie des codes NAF</h3>
                <p className="text-sm text-gray-600">
                  Cliquez sur les flèches pour développer les niveaux, puis sélectionnez les codes à exclure
                </p>
              </div>
              
              {/* Search bar */}
              <div className="relative">
                <MagnifyingGlassIcon className="absolute top-1/2 left-3 h-5 w-5 -translate-y-1/2 transform text-gray-400" />
                <input
                  type="text"
                  placeholder="Rechercher un code ou une activité..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-80 rounded-lg border border-gray-300 py-2 pr-4 pl-10 focus:border-rgpd-blue focus:ring-2 focus:ring-rgpd-blue"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute top-1/2 right-3 -translate-y-1/2 transform text-gray-400 hover:text-gray-600"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
          </div>

          {isLoading ? (
            <div className="p-4">
              <Skeleton count={10} height={48} className="mb-2" />
            </div>
          ) : error ? (
            <div className="p-8 text-center">
              <Badge mode="error" text={error} />
              <button
                onClick={fetchData}
                className="mt-4 rounded bg-rgpd-blue px-4 py-2 text-white hover:bg-rgpd-blue-dark"
              >
                Réessayer
              </button>
            </div>
          ) : filteredHierarchy && Object.keys(filteredHierarchy).length > 0 ? (
            <div className="max-h-[600px] overflow-y-auto">
              {Object.values(filteredHierarchy).map((node) => renderNafNode(node))}
            </div>
          ) : searchQuery ? (
            <div className="p-8 text-center">
              <Badge mode="warning" text={`Aucun code NAF trouvé pour "${searchQuery}"`} />
            </div>
          ) : (
            <div className="p-8 text-center">
              <Badge mode="warning" text="Aucun code NAF disponible" />
            </div>
          )}
        </div>

        {/* Loading overlay */}
        {isSaving && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
            <div className="flex items-center gap-3 rounded-lg bg-white p-6 shadow-xl">
              <Spinner size="md" />
              <span>Enregistrement en cours...</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default NafExclusion;
